import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import React, { createContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CopyViewModal } from '../components/Views/CopyViewModal';
import { useView } from '../hooks/useView';

type CopyViewModalArgs = {
  existingViewId: string;
};

type CopyViewModalContext = {
  open: boolean;
  openModal: (args: CopyViewModalArgs) => void;
  closeModal: () => void;
};

export const CopyViewModalContext = createContext<
  CopyViewModalContext | undefined
>(undefined);

type CopyViewModalProviderProps = {
  children: React.ReactNode;
};

export const CopyViewModalProvider = ({
  children,
}: CopyViewModalProviderProps) => {
  const { organizationId, projectId } = useParams();
  if (!organizationId || !projectId) {
    throw new Error(
      "CopyViewModalContent component was rendered in a page with url that doesn't have projectId or organizationId",
    );
  }

  const [isOpen, open, close] = useOpenClose();
  const [existingViewId, setExistingViewId] = useState<string>();
  const { view: existingView, isLoading } = useView({
    organizationId,
    projectId,
    viewId: existingViewId,
  });

  const contextValue = useMemo(
    () => ({
      closeModal: () => {
        close();
        setExistingViewId(undefined);
      },
      open: isOpen,
      openModal: ({ existingViewId: viewId }: CopyViewModalArgs) => {
        setExistingViewId(viewId);
        open();
      },
    }),
    [isOpen, close, open],
  );

  return (
    <CopyViewModalContext.Provider value={contextValue}>
      {isOpen && existingView && (
        <CopyViewModal
          isLoading={isLoading}
          onClose={contextValue.closeModal}
          existingView={existingView}
        />
      )}
      {children}
    </CopyViewModalContext.Provider>
  );
};
